import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useSchoolSettings } from '@/context/SchoolSettingsContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Building2, Palette, Mail, Phone, MapPin, Image, Save, RotateCcw, Eye } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SchoolSettingsPanel() {
  const { getAuthHeaders } = useAuth();
  const { settings: currentSettings, refreshSettings } = useSchoolSettings();
  const [settings, setSettings] = useState({
    school_name: '',
    school_subtitle: '',
    logo_url: '',
    primary_color: '#6366f1',
    secondary_color: '#D4A017',
    header_bg_color: '#1B1A39',
    contact_email: '',
    contact_phone: '',
    address: '',
  });
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => {
    if (currentSettings) {
      setSettings(currentSettings);
    }
  }, [currentSettings]);

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/school-settings`, settings, {
        headers: getAuthHeaders()
      });
      toast.success('Мектеп баптаулары сақталды!');
      refreshSettings();
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Сақтау кезінде қате болды');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSettings(currentSettings);
    toast.info('Өзгерістер қайтарылды');
  };

  return (
    <Card className="border-0 shadow-xl">
      <CardHeader className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-t-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Building2 className="w-6 h-6" />
            </div>
            <div>
              <CardTitle className="text-xl">Мектеп баптаулары</CardTitle>
              <CardDescription className="text-white/80">
                Мектеп атауы, логосы және түстерін өзгертіңіз
              </CardDescription>
            </div>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleReset}
              className="bg-white/20 border-white/30 text-white hover:bg-white/30"
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              Қайтару
            </Button>
            <Button 
              size="sm" 
              onClick={handleSave}
              disabled={saving}
              className="bg-white text-indigo-600 hover:bg-white/90"
            >
              <Save className="w-4 h-4 mr-1" />
              {saving ? 'Сақталуда...' : 'Сақтау'}
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="p-6">
        <Tabs defaultValue="general" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="general" className="gap-2">
              <Building2 className="w-4 h-4" />
              Жалпы
            </TabsTrigger>
            <TabsTrigger value="appearance" className="gap-2">
              <Palette className="w-4 h-4" />
              Түстер
            </TabsTrigger>
            <TabsTrigger value="contact" className="gap-2">
              <Mail className="w-4 h-4" />
              Байланыс
            </TabsTrigger>
          </TabsList>

          {/* General Tab */}
          <TabsContent value="general" className="space-y-6">
            <div className="grid gap-6">
              <div className="space-y-2">
                <Label htmlFor="school_name" className="text-sm font-semibold">
                  Мектеп атауы
                </Label>
                <Input
                  id="school_name"
                  value={settings.school_name}
                  onChange={(e) => handleChange('school_name', e.target.value)}
                  placeholder="Мысалы: Qzylorda Er BIL"
                  className="text-lg"
                />
                <p className="text-xs text-muted">Жоғарғы панельде көрінеді</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="school_subtitle" className="text-sm font-semibold">
                  Қосымша жазу (слоган)
                </Label>
                <Input
                  id="school_subtitle"
                  value={settings.school_subtitle}
                  onChange={(e) => handleChange('school_subtitle', e.target.value)}
                  placeholder="Мысалы: Мектеп жетістіктерін басқару жүйесі"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="logo_url" className="text-sm font-semibold flex items-center gap-2">
                  <Image className="w-4 h-4" />
                  Лого URL
                </Label>
                <Input
                  id="logo_url"
                  value={settings.logo_url}
                  onChange={(e) => handleChange('logo_url', e.target.value)}
                  placeholder="https://example.com/logo.png"
                />
                <p className="text-xs text-muted">
                  Логоны imgur.com немесе cloudinary.com сайтына жүктеп, URL-ін осында қойыңыз
                </p>
                
                {settings.logo_url && (
                  <div className="mt-3 p-4 bg-slate-100 rounded-lg">
                    <p className="text-xs text-muted mb-2">Алдын ала көру:</p>
                    <img 
                      src={settings.logo_url} 
                      alt="Logo preview" 
                      className="w-16 h-16 object-contain"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Appearance Tab */}
          <TabsContent value="appearance" className="space-y-6">
            <div className="grid gap-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="primary_color" className="text-sm font-semibold">
                    Негізгі түс
                  </Label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      id="primary_color"
                      value={settings.primary_color}
                      onChange={(e) => handleChange('primary_color', e.target.value)}
                      className="w-12 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={settings.primary_color}
                      onChange={(e) => handleChange('primary_color', e.target.value)}
                      className="flex-1"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="secondary_color" className="text-sm font-semibold">
                    Қосымша түс
                  </Label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      id="secondary_color"
                      value={settings.secondary_color}
                      onChange={(e) => handleChange('secondary_color', e.target.value)}
                      className="w-12 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={settings.secondary_color}
                      onChange={(e) => handleChange('secondary_color', e.target.value)}
                      className="flex-1"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="header_bg_color" className="text-sm font-semibold">
                    Header фоны
                  </Label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      id="header_bg_color"
                      value={settings.header_bg_color}
                      onChange={(e) => handleChange('header_bg_color', e.target.value)}
                      className="w-12 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={settings.header_bg_color}
                      onChange={(e) => handleChange('header_bg_color', e.target.value)}
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>

              {/* Preview */}
              <div className="mt-6">
                <Label className="text-sm font-semibold mb-3 block">Алдын ала көру</Label>
                <div 
                  className="rounded-lg p-4 flex items-center gap-4"
                  style={{ backgroundColor: settings.header_bg_color }}
                >
                  {settings.logo_url ? (
                    <img src={settings.logo_url} alt="Logo" className="w-12 h-12 object-contain" />
                  ) : (
                    <div 
                      className="w-12 h-12 rounded-lg flex items-center justify-center text-white text-xl font-bold"
                      style={{ backgroundColor: settings.primary_color }}
                    >
                      {settings.school_name?.charAt(0) || 'M'}
                    </div>
                  )}
                  <div>
                    <h3 className="font-bold text-lg" style={{ color: 'white' }}>
                      {settings.school_name || 'Merit Plus'}
                    </h3>
                    <p className="text-sm" style={{ color: settings.secondary_color }}>
                      {settings.school_subtitle || 'Мектеп жүйесі'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Contact Tab */}
          <TabsContent value="contact" className="space-y-6">
            <div className="grid gap-6">
              <div className="space-y-2">
                <Label htmlFor="contact_email" className="text-sm font-semibold flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Email
                </Label>
                <Input
                  id="contact_email"
                  type="email"
                  value={settings.contact_email}
                  onChange={(e) => handleChange('contact_email', e.target.value)}
                  placeholder="school@example.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contact_phone" className="text-sm font-semibold flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Телефон
                </Label>
                <Input
                  id="contact_phone"
                  value={settings.contact_phone}
                  onChange={(e) => handleChange('contact_phone', e.target.value)}
                  placeholder="+7 (XXX) XXX-XX-XX"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="address" className="text-sm font-semibold flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Мекенжай
                </Label>
                <Input
                  id="address"
                  value={settings.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  placeholder="Қала, көше, үй"
                />
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
